import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Title } from "../../Components/Atoms";
import { ReactTable, DatePicker } from '../../Components/Molecules';
import { DATE_OPTIONS, DATE_RANGES, DEFAULT_DATE } from '../../Library/constants';
import { getISODate, getDateStart, getDateEnd } from '../../Library/datetime';
import { getRouteStats, getDrivers } from '../../APIs';
import { StoreContext } from '../../Library/contexts';
import { Button, Spinner } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import PATHS from '../../Routes/paths';
import { AddIcon, DownloadIcon } from '../../Assets/images/svgs';
import { formatSecondsToHHMMSS } from '../../Library/datetime';
import "./style.css";

const DriverManagement = () => {
  // Consuming filters from StoreContext for Date Range
  const { userVehicles, filters, setDrivers, driverManagementCache, setDriverManagementCache } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [driverStats, setDriverStats] = useState([]);
  const navigate = useNavigate();

  // 1. Fetch Drivers (SubUsers) - Once or if missing
  useEffect(() => {
    if (driverManagementCache.subUsers) return;

    setLoading(true);
    getDrivers((err, drivers) => {
        if (drivers && !err) {
            setDrivers(drivers.map(d => ({ ...d, name: d.nm || `Driver ${d.idx}` })));
            setDriverManagementCache(prev => ({ ...prev, subUsers: drivers }));
        } else {
            console.error(err);
            setLoading(false);
        }
    });
  }, []); // Mount only

  // 2. Fetch Stats & Merge - When Drivers, Vehicles, or Range changes
  useEffect(() => {
    const subUsers = driverManagementCache.subUsers;
    if (!subUsers) return; // Wait for drivers

    const vids = userVehicles?.map(v => v.idx) || [];
    const vidsHash = vids.sort().join(',');

    // Check Cache
    if (
        driverManagementCache.stats &&
        driverManagementCache.statsRange === filters.range &&
        driverManagementCache.vidsHash === vidsHash
    ) {
        setDriverStats(driverManagementCache.stats);
        setLoading(false);
        return;
    }

    // If no vehicles, show basic driver list
    if (!vids.length) {
        processMergedData(subUsers, []);
        setLoading(false);
        return;
    }

    setLoading(true);
    getRouteStats({ vids, fromTs: getDateRangeFromFilters().fromTs, toTs: getDateRangeFromFilters().toTs }, (statsErr, statsData) => {
         setLoading(false);
         if (!statsErr) {
             const merged = processMergedData(subUsers, statsData || [], true); // Pass true to indicate return
             setDriverManagementCache(prev => ({
                 ...prev,
                 stats: merged,
                 statsRange: filters.range,
                 vidsHash: vidsHash
             }));
         } else {
             console.error(statsErr);
             processMergedData(subUsers, []); // Fallback
         }
    });

  }, [driverManagementCache.subUsers, userVehicles, filters.range]);

  /* Helper to get yesterday's start and end timestamps */
  /* Helper to get start and end timestamps from Context Filters */
  const getDateRangeFromFilters = () => {
    const range = filters.range || DATE_OPTIONS.YESTERDAY;
    const dateRange = DATE_RANGES[range];
    
    // Handle Custom Range
    if (range === DATE_OPTIONS.CUSTOM) {
       return { fromTs: dateRange.from, toTs: dateRange.to };
    }

    // Handle Predefined Ranges (which might be dynamic or static 1/2 etc)
    // Assuming DATE_RANGES values are timestamps or special flags. 
    // For standard ranges like YESTERDAY, the constants usually hold timestamps updated on app load or logic to calculate.
    // However, looking at DatePicker.jsx, it initializes state using DATE_RANGES[...].
    // Let's assume DATE_RANGES is up to date or we can recalculate.
    // Safety check:
    let from = dateRange.from;
    let to = dateRange.to;

    // Recalculate if it's a relative range identifier (common pattern in this codebase)
    if (range === DATE_OPTIONS.YESTERDAY && typeof from !== 'number') {
        // Fallback or recalculate if constants aren't dynamic
        // But usually they are. We will trust the constants for now as DatePicker does.
    }

    return { fromTs: from, toTs: to };
  };



  const processMergedData = (drivers, statsData) => {
    const statsArray = Array.isArray(statsData) ? statsData : Object.values(statsData);
    
    // Create a map of driverId -> aggregated stats
    const driverStatsMap = {};

    statsArray.forEach(vehStat => {
       // dayStats.dst is vehicle total distance. 
       // If a driver drove this vehicle, do we attribute WHOLE day distance to them?
       // `RouteSummary` attributes `dayStats.dst` to the vehicle.
       // Here we are aggregating by DRIVER.
       // If a vehicle had multiple drivers? 
       // `getRouteDailyStats` returns stats per vehicle.
       // `routes` array has `driverId`.
       // If we want "Total Dist" for a DRIVER, we should sum up distance of TRIPS they did?
       // BUT `RouteSummary` uses `dayStats.dst` which is total GPS distance.
       // If I am strictly following `RouteSummary` logic but for Drivers:
       // The user said "add data from getroutedailystats into driver".
       // If `RouteSummary` shows "Total Dist" as independent of route (GPS dist),
       // then for a driver, it should be the distance they drove?
       // `dayStats` is usually for the whole day for the vehicle.
       // If we assume 1 driver per vehicle per day (mostly), we can map it.
       // But if multiple drivers, attributing `dayStats.dst` to all might be double counting or wrong.
       // HOWEVER, user wants "Total Dist." column.
       // In `RouteSummary`, it is `totalTravelledDist`.
       // I will assume for now that I should sum `dayStats.dst` for the vehicles the driver is associated with? 
       // OR, maybe "Total Dist." in this context means "Actual Route Dist"? 
       // User requested BOTH "Actual Route Dist." AND "Total Dist.".
       // In `RouteSummary`, "Actual Route Dist" = sum of route distances. "Total Dist" = `dayStats.dst`.
       // I will attempt to attribute `dayStats.dst` to the driver who drove it.
       // If multiple drivers on same vehicle, this is ambiguous. 
       // But let's look at `vehStat` structure. `routes` has `driverId`.
       // I will attribute `dayStats.dst` to the driver found in `routes`. 
       // If multiple drivers in valid routes for one vehicle? 
       // I'll just add `dayStats.dst` to the FIRST driver found, or split it?
       // For simplicity and standard use case (1 driver), I will add it to the driver of the routes.
       
       let driverIdForDay = null;
       /* 
          Data Mapping Fix:
          - Iterate `vehStat.routes`
          - Iterate `route.tripStats` (where driver data actually lives)
       */
       if (vehStat.routes && Array.isArray(vehStat.routes)) {
         vehStat.routes.forEach(route => {
           if (route.tripStats && Array.isArray(route.tripStats)) {
              route.tripStats.forEach(trip => {
                  let dId = trip.driverId;
                  if (!dId) dId = "unknown";
                  else dId = String(dId); // Coerce to string for consistent matching

                  // Helper to init stats
                  const initStats = () => ({
                      routeCount: 0,
                      tripCount: 0,
                      expectedDist: 0,
                      actualDist: 0, 
                      deviation: 0,   
                      duration: 0,
                      totalDist: 0,
                      routes: new Set(),
                  });

                  if (!driverStatsMap[dId]) {
                    driverStatsMap[dId] = initStats();
                  }
                  
                  // Aggregate Per Driver
                  // Note: Route Count might be inflated if we count every trip's route. 
                  // Ideally we want unique routes per driver.
                  // For now, simpler aggregation:

                  driverStatsMap[dId].routes.add(route.routeId);
                  driverStatsMap[dId].routeCount = driverStatsMap[dId].routes.size;

                  driverStatsMap[dId].tripCount += 1; 
                  
                  // Per user request: Use routeDst for Expected (Meters), dst for Actual (Meters), deviated (Meters).
                  // route.routeDst is usually the full route length in meters.
                  driverStatsMap[dId].expectedDist += (route.routeDst || 0); 
                  
                  driverStatsMap[dId].actualDist += (trip.dst || 0); // trip.dst is confirmed Meters
                  
                  // User explicitly said "deviated is in meters". So NO multiplier.
                  driverStatsMap[dId].deviation += (trip.deviated || 0); 
                  
                  driverStatsMap[dId].duration += (trip.dur || trip.duration || 0);
                  
                  // totalDist matches Actual Dist (GPS distance)
                  driverStatsMap[dId].totalDist += (trip.dst || 0);
              });
           }
         });
       }
       
       // Attribute dayStats.dst to the driver(s). 
       // If multiple drivers, this is tricky. Logic in RouteSummary is per VEHICLE.
       // Here it is per DRIVER.
       // If I assign to `driverIdForDay` (which is one of them), it picks one.
       // Let's assume the driver on the route is the one who drove the total distance.
       if (driverIdForDay && vehStat.dayStats && vehStat.dayStats.dst) {
          driverStatsMap[driverIdForDay].totalDist += vehStat.dayStats.dst;
       }
    });

    // Merge with drivers list
    const mergedData = drivers.map(driver => {
        const stats = driverStatsMap[driver.idx] || {
            routeCount: 0,
            tripCount: 0,
            expectedDist: 0,
            actualDist: 0,
            deviation: 0,
            duration: 0,
            totalDist: 0
        };

        return {
            ...driver,
            driverId: driver.idx,
            driverName: driver.nm || `Driver ${driver.idx}`,
            ...stats
        };
    });

    setDriverStats(mergedData);
    return mergedData;
  };

  const columns = useMemo(() => [
    { 
      Header: "Driver", 
      accessor: "driverName",
      Cell: ({ row, value }) => (
        <span 
            className="driver-name-link" 
            onClick={() => navigate(`${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_SUMMARY}?idx=${row.original.driverId}`)}
            style={{ cursor: "pointer", fontWeight: "600" }} 
        >
            {value}
        </span>
      )
    },
    { Header: "Routes Travelled", accessor: "routeCount" },
    { Header: "Trips", accessor: "tripCount" },
    { 
      Header: "Expected Route Dist.", 
      accessor: "expectedDist",
      Cell: ({ value }) => `${(value / 1000).toFixed(2)} kms`
    },
    { 
      Header: "Actual Route Dist.", 
      accessor: "actualDist",
      Cell: ({ value }) => `${(value / 1000).toFixed(2)} kms`
    },
    { 
      Header: "Deviated Dist.", 
      accessor: "deviation",
      Cell: ({ value }) => `${(value / 1000).toFixed(2)} kms`
    },
    { 
      Header: "Total Dist.", 
      accessor: "totalDist",
      Cell: ({ value }) => `${(value / 1000).toFixed(2)} kms`
    },
    { 
      Header: "Duration", 
      accessor: "duration",
      Cell: ({ value }) => value ? formatSecondsToHHMMSS(value) : "00:00:00"
    }
  ], []);

  /* Matching Subusers styled structure */
  const breadcrumbPaths = [
    { idx: 1, name: "Driver Stats", isCurrentPage: true },
  ];

  return (
    <div className="panel-main driver-management-page">
      <div className="btn-cnt">
        <div className="bc-cnt">
          <Breadcrumb paths={breadcrumbPaths} />
        </div>
         {/* Date Picker and Button Container */}
          <div className="dm-actions-row">
              <div className="dm-left-actions">
                 <div className="dm-date-picker">
                    <DatePicker customMaxDate={getISODate(DATE_RANGES[DATE_OPTIONS.YESTERDAY].from)} />
                 </div>
              </div>
          </div>
      </div>

      <div className="driver-stats-container">
        <div className="driver-stats-table-header">
          <Title>Driver Stats</Title>
          <div className="driver-stats-btn-container">
            <Button 
              className="dm-download-btn" 
              leftIcon={<DownloadIcon />} 
              variant="ghost" 
              onClick={() => {
                  if (!driverStats.length) return;
                  const headers = ["Driver", "Routes Travelled", "Trips", "Expected Route Dist. (kms)", "Actual Route Dist. (kms)", "Deviated Dist. (kms)", "Total Dist. (kms)", "Duration"];
                  const csvContent = [
                      headers.join(","),
                      ...driverStats.map(row => [
                          `"${row.driverName}"`,
                          row.routeCount,
                          row.tripCount,
                          (row.expectedDist / 1000).toFixed(2),
                          (row.actualDist / 1000).toFixed(2),
                          (row.deviation / 1000).toFixed(2),
                          (row.totalDist / 1000).toFixed(2),
                          row.duration ? formatSecondsToHHMMSS(row.duration) : "00:00:00"
                      ].join(","))
                  ].join("\n");
                  
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.setAttribute("href", url);
                  link.setAttribute("download", `driver_stats_${new Date().toISOString().split('T')[0]}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
              }}
          >
            Download
            </Button>
            <Button 
              className="add-driver-btn" 
              leftIcon={<AddIcon />}
              onClick={() => navigate(`${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_MANAGEMENT_ADD}`)}
            >
              Add New Driver
            </Button>
          </div>
        </div>
        {loading ? <Spinner className="stats-table-loader" /> : (
           <ReactTable
             data={driverStats} 
             columns={columns} 
             showSearch={true}
             pageSize={10} 
           />
        )}
      </div>
    </div>
  );
};

export default DriverManagement;
